import React, { useEffect, useState } from "react";
import { getTransparencyById, getTransparency } from "../../app/api";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const HTMLTextView = ({ htmlContent }: any) => {
  if (!htmlContent) return null;
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};

export default function TransparencyDetail() {
  const [loading, setLoading] = useState(false);
  const [transparency, setTransparency] = useState<any>(null);
  const [children, setChildren] = useState<any[]>([]);
  const params = useParams();

  useEffect(() => {
    setLoading(true);
    
    // Fetch transparency detail
    getTransparencyById(params.id)
      .then((res: any) => {
        if (res && res.data) {
          setTransparency(res.data);
          
          // Fetch children if exists
          if (res.data._id) {
            getTransparency({ parent_id: res.data._id, limit: 50, offset: 0 })
              .then((childRes: any) => {
                if (childRes && childRes.data && childRes.data.results) {
                  setChildren(childRes.data.results);
                }
              })
              .catch((err) => {
                console.error("Error fetching children:", err);
              });
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching transparency:", err);
      })
      .finally(() => {
        setLoading(false);
      });
    
    window.document.body.scrollTop = 0;
    window.document.documentElement.scrollTop = 0;
  }, [params.id]);

  if (loading) {
    return (
      <div className="bg-white py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="w-full h-full fixed top-0 left-0 bg-white opacity-75 z-50">
            <div className="flex justify-center items-center mt-[50vh]">
              <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2">
                <svg
                  aria-hidden="true"
                  className="w-24 h-24 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!transparency) {
    return (
      <div className="bg-white py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center text-gray-500 py-12">
            Ил тод байдлын мэдээлэл олдсонгүй.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <Link
          to="/transparency"
          className="inline-flex items-center text-sm font-semibold text-[#08B3EC] mb-8 hover:text-[#001447]"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Буцах
        </Link>

        <div className="mx-auto max-w-3xl">
          <h1 className="text-3xl font-bold tracking-tight text-[#001447] sm:text-4xl mb-8">
            {transparency.name}
          </h1>

          {transparency.content && (
            <div className="prose prose-lg max-w-none mb-8">
              <HTMLTextView htmlContent={transparency.content} />
            </div>
          )}

          {transparency.pdf_url && (
            <div className="mb-8">
              <div className="w-full border border-gray-200 rounded-lg overflow-hidden shadow-lg">
                <iframe
                  src={transparency.pdf_url}
                  className="w-full h-[600px] md:h-[800px]"
                  title="PDF Viewer"
                />
              </div>
            </div>
          )}

          {transparency.link && (
            <div className="mb-8">
              <a
                href={transparency.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border-2 border-[#08B3EC] text-[#08B3EC] font-semibold rounded-lg hover:bg-[#08B3EC] hover:text-white transition-colors"
              >
                Холбоос нээх
              </a>
            </div>
          )}

          {children.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-[#001447] mb-6">
                Дэд ангилал
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {children.map((child: any) => (
                  <Link
                    key={child._id || child.id}
                    to={`/transparency/${child._id || child.id}`}
                    className="p-6 border border-gray-200 rounded-lg hover:border-[#08B3EC] hover:shadow-md transition-all"
                  >
                    <h3 className="text-lg font-semibold text-[#001447] mb-2">
                      {child.name}
                    </h3>
                    {child.content && (
                      <p className="text-gray-600 text-sm line-clamp-3">
                        <HTMLTextView htmlContent={child.content.substring(0, 150) + '...'} />
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

